import { HOST_TABS } from "@/lib/host-panel/host-tabs";

/**
 * Sanitizes a redirect path taken from user-controlled input, such as a `?next=` query param.
 *
 * Refuses anything that could execute or leave the origin: the `javascript:` scheme, absolute and
 * protocol-relative URLs, the backslash variants some browsers normalize to `//`, and paths outside
 * the allowed scopes. The tests enumerate the full list.
 *
 * @param value - The raw `?next=` value from the URL
 * @param allowedPrefixes - Path prefixes the redirect must match at least one of
 * @param fallback - Where to go if the value is invalid
 */
export function sanitizeRedirect(
  value: string | null | undefined,
  allowedPrefixes: string[],
  fallback: string,
): string {
  if (!value) return fallback;

  // Block protocol-relative URLs (//evil.com)
  if (value.startsWith("//")) return fallback;

  // Block any scheme (javascript:, https:, data:, etc.)
  if (/^[a-z][a-z0-9+\-.]*:/i.test(value)) return fallback;

  // Block backslash (some browsers normalize \ to /)
  if (value.includes("\\")) return fallback;

  // Block a fragment: it never belongs in a server redirect target, and `/admin#...`
  // would otherwise pass the segment-boundary check while escaping the `/admin`,
  // `/admin/`, `/admin?` contract the sanitized target must honor.
  if (value.includes("#")) return fallback;

  // Must match at least one allowed prefix
  const isAllowed = allowedPrefixes.some((prefix) => matchesPrefix(value, prefix));
  if (!isAllowed) return fallback;

  return value;
}

/**
 * Whether the path is the prefix itself or something under it.
 *
 * The prefix has to end on a segment boundary: a plain `startsWith` also accepts
 * `/anfitriaoqualquercoisa` for `/anfitriao`, which is a different route that merely begins
 * with the same letters.
 */
function matchesPrefix(value: string, prefix: string): boolean {
  if (!value.startsWith(prefix)) return false;

  const next = value.charAt(prefix.length);

  return next === "" || next === "/" || next === "?";
}

// ─── Pre-configured validators per auth flow ─────────────────────────────────

/**
 * Allowed redirect targets after customer login (existing user, not signup).
 *
 * The panel's own tabs, named from `HOST_TABS` so the list cannot drift from the routes that
 * exist, plus the list creation and the checkout. `/anfitriao` as a whole is deliberately NOT
 * allowed: it would send someone who just logged in to any address under it, including ones
 * with no page behind them — a 404 as the first screen after signing in.
 *
 * `/produtos` is here so someone who was reading a piece when their session died comes back to
 * **that piece** after logging in, instead of to the panel (`addToMyListHref`). It is a public
 * page either way, so allowing it grants nothing; what it buys is the person not losing their
 * place. The segment-boundary rule below keeps the `?contexto=` the page needs.
 */
const CUSTOMER_ALLOWED_PREFIXES = [
  ...HOST_TABS.map((tab) => tab.href),
  "/anfitriao/nova-lista",
  "/produtos",
  "/checkout",
];
const CUSTOMER_FALLBACK = "/anfitriao/dashboard";

/** Allowed redirect targets after internal (admin/backoffice) login. */
const ADMIN_ALLOWED_PREFIXES = ["/admin"];
const ADMIN_FALLBACK = "/admin";

/**
 * Sanitizes redirect for customer login flow.
 * Accepts paths under `/anfitriao/*` or `/checkout/*`.
 *
 * The fallback is the caller's to choose (`ONBOARDING_DESTINATION`): a door opened to pay for a
 * gift ends at the checkout, and sending that person to the panel because the `?next=` was
 * missing would be the same loss the guest flow exists to avoid.
 */
export function sanitizeCustomerRedirect(
  value: string | null | undefined,
  fallback: string = CUSTOMER_FALLBACK,
): string {
  return sanitizeRedirect(value, CUSTOMER_ALLOWED_PREFIXES, fallback);
}

/**
 * Sanitizes redirect for admin login flow.
 * Accepts paths under `/admin/*` only.
 */
export function sanitizeAdminRedirect(value: string | null | undefined): string {
  return sanitizeRedirect(value, ADMIN_ALLOWED_PREFIXES, ADMIN_FALLBACK);
}
